import { ReactComponent as PlayButtonSVG } from '@/assets/images/svg/buttons/play-button.svg';
import Image from '@/components/Image';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import isMobileFn from 'is-mobile';
import { FC, useEffect, useMemo, useState } from 'react';
import YouTube, { YouTubeProps } from 'react-youtube';
import styled, { css } from 'styled-components';

const PlayButton = styled(PlayButtonSVG)`
  position: absolute;
  bottom: 10%;
  right: 8%;
  width: 6%;
  z-index: 2;
  transform: scale(1);
  transition: transform 0.4s ease-in-out, box-shadow 0.4s ease-in-out;
  box-shadow: 0 0 10px 0 ${hexToRGBA(COLORS.BLACK, 0.25)};
  border-radius: 100%;
  pointer-events: none;
`;

interface FadeProps {
  show: boolean;
}

const Fade = styled.div<FadeProps>`
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 1;

  width: 100%;
  height: 25%;

  background: linear-gradient(0deg, var(--video-fade-color, transparent) 0%, transparent 100%);
  opacity: 0.5;
  transition: opacity 0.4s ease-in-out;

  pointer-events: none;

  ${(props) =>
    !props.show &&
    css`
      opacity: 0;
    `}
`;

interface VideoWrapperProps {
  showPointer: boolean;
}

export const VideoWrapper = styled.div<VideoWrapperProps>`
  position: relative;
  height: 0;
  padding-bottom: ${(1080 / 1920) * 100}%;

  background-color: #000;

  > img {
    object-fit: cover;
    object-position: center;
  }

  > div:first-child {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  ${(props) =>
    props.showPointer &&
    css`
      cursor: pointer;
    `}

  &:hover {
    ${PlayButton} {
      transform: scale(1.15);
      box-shadow: 0 0 15px 0 ${hexToRGBA(COLORS.BLACK, 0.15)};
    }

    ${Fade} {
      opacity: 0;
    }
  }
`;

interface YouTubeVideoProps extends YouTubeProps {
  coverSrc?: string;
  onInitialPlay?: () => void;
  showPlayButton?: boolean;
  className?: string;
  autoplay?: boolean;
}

const YouTubeVideo: FC<YouTubeVideoProps> = ({
  className,
  coverSrc,
  onInitialPlay,
  showPlayButton,
  autoplay,
  onEnd,
  onPlay,
  ...props
}) => {
  const [showCover, setShowCover] = useState(true);
  const [videoPlayed, setVideoPlayed] = useState(false);
  const isMobile = useMemo(isMobileFn, []);

  /* Reset when video changes */
  useEffect(() => {
    setShowCover(true);
    setVideoPlayed(false);
  }, [props.videoId]);

  useEffect(() => {
    if (autoplay || isMobile) setShowCover(false);
  }, []);

  return (
    <VideoWrapper className={className} showPointer={showCover}>
      {showCover ? (
        <Image
          src={coverSrc ?? `https://img.youtube.com/vi/${props.videoId}/hqdefault.jpg`}
          fill
          alt=""
          onClick={() => {
            setShowCover(false);
          }}
        />
      ) : (
        <YouTube
          {...props}
          opts={{
            ...props.opts,
            width: props.opts?.width || '100%',
            height: props.opts?.height || '100%',
            playerVars: {
              autoplay: isMobile ? 0 : 1,
              rel: 0,
              modestbranding: 1,
              disablekb: 1,
              ...props?.opts?.playerVars,
            },
          }}
          onPlay={(event) => {
            if (!videoPlayed) {
              setVideoPlayed(true);

              if (onInitialPlay) onInitialPlay();
            }

            if (onPlay) onPlay(event);
          }}
          onEnd={(event) => {
            if (!isMobile) setShowCover(true);
            if (onEnd) onEnd(event);
          }}
        />
      )}

      <Fade show={showCover} />

      {showPlayButton && showCover && <PlayButton title="Play button" />}
    </VideoWrapper>
  );
};

YouTubeVideo.defaultProps = {
  showPlayButton: true,
  autoplay: false,
};

export default YouTubeVideo;
